import * as React from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * MobileListCard — the canonical row UI for ANY table-like surface
 * rendered on mobile (Asana / Linear / Notion style).
 *
 * Layout slots:
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ [leading]   TITLE                  [amount] │
 *   │             subtitle                        │
 *   │             [badge] [badge]    [trailing] ▸ │
 *   │             ── meta key-value strip ──      │
 *   │   meta·label   value · meta·label   value   │
 *   └─────────────────────────────────────────────┘
 *
 * Every slot is optional so the card adapts to the table at hand —
 * an Items row uses `leading` (thumb) + `title` + `subtitle` (SKU) +
 * `meta` (cost / price) + `amount` (retail) + `trailing` (chevron),
 * while a Tasks row uses `leading` (checkbox) + `title` + `badges`
 * (status, priority) + `meta` (due date).
 *
 * Interaction:
 *   * `onTap` — primary navigation (matches DataTable's onRowClick).
 *   * `onLongPress` — optional, used by selection mode to enter
 *     multi-select from a single tap-and-hold (Asana/Things 3 idiom).
 *     Falls back gracefully on devices without long-press support.
 *   * `selected` + `onToggleSelect` — when selection mode is active,
 *     the card auto-promotes its leading slot to a checkbox (or wraps
 *     the existing leading content with one) so the user doesn't have
 *     to fish for the tap target.
 *
 * Hit-target sizing meets Apple's HIG (44 × 44 pt minimum) for the
 * leading checkbox, trailing chevron, and the whole row itself (56 px
 * min height including padding).
 */

export interface MobileListCardMetaItem {
  /** Short label (e.g. "Cost", "Due", "Source"). Rendered in
   *  `uppercase tracking-wider text-[10px]` muted. */
  label: string;
  /** The value. Can be plain text, a number, or any React node — but
   *  prefer to keep it to a single line; the meta strip auto-truncates
   *  with ellipsis on overflow. */
  value: React.ReactNode;
}

export interface MobileListCardProps {
  // ── Content slots ────────────────────────────────────────────
  leading?: React.ReactNode;
  /** Required. The primary identifier of the row. Auto-truncated. */
  title: React.ReactNode;
  /** Optional secondary line beneath the title. Two-line clamp. */
  subtitle?: React.ReactNode;
  /** Optional chip row beneath the title. Auto-wraps when many. */
  badges?: React.ReactNode;
  /** Two-column key-value strip beneath everything else. Best for
   *  numeric fields (cost, qty, balance) that benefit from a clear
   *  label and tabular numerals. */
  meta?: MobileListCardMetaItem[];
  /** Right-aligned bold amount displayed across from the title.
   *  Designed for financial / quantitative columns. The `tone`
   *  hint applies the brand's positive/negative/neutral palette. */
  amount?: {
    value: React.ReactNode;
    tone?: 'positive' | 'negative' | 'neutral';
    /** Optional caption rendered below the amount (currency code,
     *  unit, "of N"). Kept tiny so the amount stays prominent. */
    caption?: React.ReactNode;
  };
  /** Optional trailing slot — renders to the right of the badge
   *  row. Most callers leave this empty so the auto-chevron shows
   *  up on tap-to-navigate; pass `null` to suppress the chevron
   *  entirely (e.g. read-only rows that aren't navigable). */
  trailing?: React.ReactNode;

  // ── Interaction ──────────────────────────────────────────────
  onTap?: () => void;
  onLongPress?: () => void;

  // ── Selection mode ───────────────────────────────────────────
  selected?: boolean;
  onToggleSelect?: () => void;
  /** When true, the card shows the selection checkbox in its
   *  leading slot regardless of whether anything is selected yet
   *  (so the operator can opt out before tapping). */
  selectionMode?: boolean;

  // ── Visual variants ──────────────────────────────────────────
  className?: string;
  /** When true, the card renders with a muted background to
   *  indicate the row is in a terminal / archived state
   *  (e.g. transactions that have been posted to QBO). */
  dimmed?: boolean;
}

/**
 * Long-press timing follows Material guidelines (500 ms). We use a
 * setTimeout from `pointerdown` and cancel on move / leave / up — a
 * native `contextmenu` listener would also work but iOS Safari fires
 * one synthesized event then nothing, which is too fragile for a
 * selection-entry gesture.
 */
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 8;

export function MobileListCard({
  leading,
  title,
  subtitle,
  badges,
  meta,
  amount,
  trailing,
  onTap,
  onLongPress,
  selected,
  onToggleSelect,
  selectionMode,
  className,
  dimmed,
}: MobileListCardProps) {
  // Long-press uses refs to avoid re-rendering on every pointer move
  // — the only state change we care about is "fired or not". The
  // `fired` ref also suppresses the synthetic click that follows the
  // pointerup so the card doesn't navigate AND select on the same
  // gesture.
  const longPressTimer = React.useRef<number | null>(null);
  const longPressFired = React.useRef(false);
  const startPoint = React.useRef<{ x: number; y: number } | null>(null);

  const clearLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    startPoint.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!onLongPress) return;
    startPoint.current = { x: e.clientX, y: e.clientY };
    longPressFired.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (longPressTimer.current === null || !startPoint.current) return;
    const dx = Math.abs(e.clientX - startPoint.current.x);
    const dy = Math.abs(e.clientY - startPoint.current.y);
    if (dx > LONG_PRESS_MOVE_TOLERANCE_PX || dy > LONG_PRESS_MOVE_TOLERANCE_PX) {
      clearLongPress();
    }
  };

  const handlePointerUp = () => {
    clearLongPress();
  };

  const handlePointerCancel = () => {
    clearLongPress();
  };

  const handleClick = (e: React.MouseEvent) => {
    // Long-press just promoted into a select event; swallow the
    // click that the browser fires alongside `pointerup` so the row
    // doesn't ALSO navigate on the same gesture.
    if (longPressFired.current) {
      longPressFired.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (selectionMode && onToggleSelect) {
      onToggleSelect();
      return;
    }
    onTap?.();
  };

  // In selection mode the leading slot becomes a *visual-only* checkbox
  // (a styled div, not a <button>). The outer card is already the
  // toggle target — embedding a real Checkbox button inside this
  // <button> wrapper would be invalid HTML and Safari ignores the
  // inner click anyway. Outside selection mode the caller's leading
  // slot (thumbnail, icon, …) wins. We never render *both* so the
  // row footprint stays exactly the same width whether selection is
  // active or not.
  const renderedLeading =
    selectionMode && onToggleSelect ? (
      <div
        role="presentation"
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded border transition-colors',
          selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-card',
        )}
      >
        {selected && <Check className="h-3.5 w-3.5" />}
      </div>
    ) : (
      leading
    );

  // Trailing defaults to a chevron when the card is tappable. Callers
  // can pass `trailing={null}` to suppress (e.g. on read-only rows).
  const renderedTrailing =
    trailing !== undefined
      ? trailing
      : onTap
        ? <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" aria-hidden />
        : null;

  // Whole-card pointer / click handlers stay on the outer button when
  // the row is tappable; an inert div is used otherwise so screen
  // readers don't announce a button that does nothing. Keeps the
  // semantics correct for read-only history rows.
  const Wrapper: React.ElementType =
    onTap || onToggleSelect ? 'button' : 'div';

  const amountToneClass =
    amount?.tone === 'positive'
      ? 'text-bright-moss-dark'
      : amount?.tone === 'negative'
        ? 'text-fresh-salmon-dark'
        : 'text-foreground';

  return (
    <Wrapper
      type={Wrapper === 'button' ? 'button' : undefined}
      onClick={Wrapper === 'button' ? handleClick : undefined}
      onPointerDown={onLongPress ? handlePointerDown : undefined}
      onPointerMove={onLongPress ? handlePointerMove : undefined}
      onPointerUp={onLongPress ? handlePointerUp : undefined}
      onPointerLeave={onLongPress ? handlePointerCancel : undefined}
      onPointerCancel={onLongPress ? handlePointerCancel : undefined}
      aria-pressed={selectionMode ? !!selected : undefined}
      className={cn(
        // Base card surface — pure white over Linen, brand border
        // radius (rounded-xl = var(--radius)). `text-left` because
        // the wrapper is sometimes a <button> which would otherwise
        // centre its content.
        'group relative flex w-full items-stretch gap-3 rounded-lg border border-border bg-card px-3.5 py-3 text-left transition-colors',
        // Active state — tactile press feedback on touch.
        (onTap || onToggleSelect) &&
          'active:bg-secondary/50 active:scale-[0.998]',
        // Selected state — selection-mode rows get a quiet accent
        // border + a faint tint so the eye can scan which cards are
        // staged for a bulk action.
        selected && 'border-old-growth bg-old-growth/5',
        // Dimmed = terminal-state row (e.g. posted-to-QBO txns).
        dimmed && 'bg-secondary/30 text-muted-foreground',
        className,
      )}
    >
      {/* Leading rail — checkbox or caller icon. Auto-aligns to the
          top so it lines up with the title baseline regardless of
          subtitle / badge presence. */}
      {renderedLeading && (
        <div className="flex shrink-0 items-start pt-0.5">
          {renderedLeading}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Top row: title + (optional) right-aligned amount. The
            title truncates with ellipsis so a long description
            doesn't push the amount off the card edge. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="truncate text-sm font-medium text-foreground">
              {title}
            </div>
            {subtitle && (
              <div className="line-clamp-2 text-xs text-muted-foreground">
                {subtitle}
              </div>
            )}
          </div>
          {amount && (
            <div className="flex shrink-0 flex-col items-end pt-0.5">
              <span
                className={cn(
                  'text-sm font-medium tabular-nums',
                  amountToneClass,
                )}
              >
                {amount.value}
              </span>
              {amount.caption && (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {amount.caption}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Badges + trailing on the same row so chevron / status
            chip / priority chip all align tidily. Hidden when both
            slots are empty so the card collapses vertically. */}
        {(badges || renderedTrailing) && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {badges}
            </div>
            {renderedTrailing && (
              <div
                className="flex shrink-0 items-center gap-2"
                // Stop click on a trailing action button from
                // bubbling up to the card and triggering onTap.
                // Callers wanting the navigate-on-tap path can
                // attach a sibling chevron via the same slot
                // without an onClick.
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('button, a, [role="button"]')) {
                    e.stopPropagation();
                  }
                }}
              >
                {renderedTrailing}
              </div>
            )}
          </div>
        )}

        {/* Meta key-value strip — used for things like Cost / Retail
            / Due / Posted by. Grid lets us pack two key-values per
            row at narrow widths and three on a roomy tablet. */}
        {meta && meta.length > 0 && (
          <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-border/60 pt-2 text-xs">
            {meta.map((m, i) => (
              <div key={`${m.label}-${i}`} className="flex min-w-0 flex-col">
                <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {m.label}
                </dt>
                <dd className="min-w-0 truncate text-foreground">
                  {m.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </Wrapper>
  );
}

/**
 * Convenience wrapper: a vertically stacked list of `MobileListCard`s
 * with consistent gaps. Used by every mobile table branch + raw lists
 * (BankAccountsPage, OrdersPage) so the visual rhythm is identical
 * across the app.
 *
 * Pass `divided` to render thin separators instead of gaps for very
 * dense list contexts (account / card lists where 1px lines read
 * cleaner than 12px gaps).
 */
export function MobileListCardStack({
  children,
  className,
  divided = false,
}: {
  children: React.ReactNode;
  className?: string;
  divided?: boolean;
}) {
  return (
    <div
      className={cn(
        divided
          ? 'flex flex-col divide-y divide-border overflow-hidden rounded-lg border border-border bg-card'
          : 'flex flex-col gap-2',
        className,
      )}
    >
      {children}
    </div>
  );
}
